/**
 * `<InlineIcd10Picker>` — thin wrapper around `<InlineCodePicker>` that wires
 * the existing ICD-10 ValueSet expansion (`https://behuman.health/fhir/ValueSet/
 * icd10cm-2026`) into the inline row picker used by the Problem List table.
 *
 * Mirrors the data layer of `DxCodingSelect` (recent-patient-diagnoses preload,
 * debounced ValueSet expansion, deduped option list) but renders through the
 * mockup-style coral monospace input + dropdown of `code  description` pairs.
 *
 * Why a separate component (not re-exporting `DxCodingSelect`):
 *  - `DxCodingSelect` is built around Mantine `Select` and renders the full
 *    `code - description` label as the visible value. The mockup wants the
 *    input to show ONLY the code in coral monospace, with description in a
 *    sibling column. Different UX needs a different visual primitive.
 *  - We keep the data-fetch logic close to its consumer here so both pickers
 *    can evolve independently (e.g. `DxCodingSelect` remains the "Add
 *    Diagnosis" standalone picker; this wrapper is row-bound).
 */
import { useEffect, useMemo, useState } from 'react'
import { useMedplum } from '@medplum/react'
import type { Condition, ValueSetExpansionContains } from '@medplum/fhirtypes'
import { useDebounce } from '@/hooks'
import { logError } from '@/utils/errorUtils'
import { InlineCodePicker, type InlineCodeOption } from './InlineCodePicker'
import { ICD10_SYSTEM } from './fhirSystems'

const ICD10_VALUE_SET_URL = 'https://behuman.health/fhir/ValueSet/icd10cm-2026'

type ExpansionNode = ValueSetExpansionContains & { contains?: ValueSetExpansionContains[] }

function flattenContains(nodes: ValueSetExpansionContains[] = []): ValueSetExpansionContains[] {
  const flat: ValueSetExpansionContains[] = []
  const queue: ExpansionNode[] = [...nodes]
  while (queue.length > 0) {
    const current = queue.shift()
    if (!current) continue
    flat.push(current)
    if (Array.isArray(current.contains) && current.contains.length > 0) {
      queue.push(...(current.contains as ExpansionNode[]))
    }
  }
  return flat
}

function dedupe(options: InlineCodeOption[]): InlineCodeOption[] {
  const seen = new Set<string>()
  const out: InlineCodeOption[] = []
  for (const o of options) {
    const key = `${o.system}|${o.code}`
    if (!o.code || seen.has(key)) continue
    seen.add(key)
    out.push(o)
  }
  return out
}

export interface InlineIcd10PickerProps {
  value?: { code: string; display?: string; system?: string }
  patientId?: string
  placeholder?: string
  onChange: (picked: InlineCodeOption) => void
  testId?: string
}

export function InlineIcd10Picker({
  value,
  patientId,
  placeholder = 'Search ICD-10...',
  onChange,
  testId,
}: InlineIcd10PickerProps) {
  const medplum = useMedplum()
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [recent, setRecent] = useState<InlineCodeOption[]>([])
  const [searchResults, setSearchResults] = useState<InlineCodeOption[]>([])
  const [hasLoadedRecent, setHasLoadedRecent] = useState(false)

  const debouncedSearch = useDebounce(search, 250)

  // Preload the patient's recent diagnoses so the dropdown is useful on first
  // focus without requiring a search query.
  useEffect(() => {
    let active = true
    const run = async () => {
      if (!patientId) {
        if (active) {
          setRecent([])
          setHasLoadedRecent(true)
        }
        return
      }
      try {
        const conditions = await medplum.searchResources('Condition', {
          subject: `Patient/${patientId}`,
          _sort: '-recorded-date,-asserted-date',
          _count: '200',
        })
        if (!active) return
        const codings: InlineCodeOption[] = []
        for (const c of conditions as Condition[]) {
          const coding = c.code?.coding?.find((co) =>
            (co.system || '').toLowerCase().includes('icd-10'),
          )
          if (coding?.code) {
            codings.push({
              code: coding.code,
              display: coding.display || coding.code,
              system: coding.system || ICD10_SYSTEM,
            })
          }
        }
        setRecent(dedupe(codings).slice(0, 15))
        setHasLoadedRecent(true)
      } catch (err) {
        logError('inline-icd10-picker', 'load recent diagnoses', err)
        if (active) setHasLoadedRecent(true)
      }
    }
    void run()
    return () => {
      active = false
    }
  }, [medplum, patientId])

  // Live ValueSet expansion against the BeHuman ICD-10 ValueSet — runs on
  // every debounced search change (and once on first mount with an empty
  // filter so the dropdown has SOMETHING to show before the user types).
  useEffect(() => {
    let active = true
    const run = async () => {
      if (patientId && !hasLoadedRecent) return

      const filter = debouncedSearch.trim()
      // When the search is empty AND we have recent diagnoses for this
      // patient, skip the ValueSet call — the dropdown will show recents.
      if (filter.length === 0 && patientId && recent.length > 0) {
        setSearchResults([])
        return
      }
      try {
        setLoading(true)
        const expanded = await medplum.valueSetExpand({
          url: ICD10_VALUE_SET_URL,
          filter,
          count: filter ? 25 : 15,
          offset: 0,
        })
        if (!active) return
        const contains = flattenContains(expanded.expansion?.contains || [])
        const opts: InlineCodeOption[] = contains
          .filter((c): c is ValueSetExpansionContains => Boolean(c.code))
          .map((c) => ({
            code: c.code as string,
            display: c.display || (c.code as string),
            system: c.system || ICD10_SYSTEM,
          }))
        setSearchResults(dedupe(opts))
      } catch (err) {
        logError('inline-icd10-picker', 'valueSetExpand', err)
        if (active) setSearchResults([])
      } finally {
        if (active) setLoading(false)
      }
    }
    void run()
    return () => {
      active = false
    }
  }, [debouncedSearch, hasLoadedRecent, medplum, patientId, recent.length])

  // Compose the dropdown options. When the user hasn't typed anything AND has
  // a recent-diagnoses list, surface those; otherwise show ValueSet results.
  // Pin the currently-selected option to the top of the list so it stays
  // available even if it falls out of the current search window.
  const options = useMemo<InlineCodeOption[]>(() => {
    const source =
      debouncedSearch.trim().length === 0 && patientId && recent.length > 0 ? recent : searchResults
    const out = value?.code
      ? dedupe([
          {
            code: value.code,
            display: value.display || value.code,
            system: value.system || ICD10_SYSTEM,
          },
          ...source,
        ])
      : source
    return out
  }, [debouncedSearch, patientId, recent, searchResults, value])

  return (
    <InlineCodePicker
      value={value}
      options={options}
      loading={loading}
      placeholder={placeholder}
      onSearchChange={setSearch}
      onChange={onChange}
      testId={testId}
    />
  )
}
